import React, { useEffect, useState } from "react";
import { Package, Save } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import useAuth from "../hooks/useAuth";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";
import "./PayscalesPage.css";

const LEVELS = [
  { label: "Level 1", hint: "0%" },
  { label: "Level 2", hint: "10%" },
  { label: "Level 3", hint: "15%" },
  { label: "Level 4", hint: "20%" },
  { label: "Level 5", hint: "25%" },
];

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function displayNumber(value) {
  if (value == null || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : "";
}

function rowPrices(cost, taxPercent) {
  if (cost === "" || cost == null) return null;
  const costNumber = Number(cost);
  const taxNumber = Number(taxPercent);
  if (!Number.isFinite(costNumber)) return null;
  const tax = Number.isFinite(taxNumber) ? taxNumber : 0;
  const companyCost = costNumber * (1 + tax / 100);
  const markups = [0, 0.1, 0.15, 0.2, 0.25];
  return {
    companyCost,
    levels: markups.map((markup) => companyCost * (1 + markup)),
  };
}

function formatMoney(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return money.format(value);
}

export default function PayscalesPage() {
  const navigate = useNavigate();
  const { isReadOnly } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    API.get("settings/payscales/")
      .then((res) => {
        if (cancelled) return;
        setRows(
          (res.data.items || []).map((item) => ({
            productId: item.product_id,
            name: item.name,
            cost: displayNumber(item.cost),
            taxPercent: displayNumber(item.tax_percent),
            level6Amount: displayNumber(item.level_6_amount),
          }))
        );
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load payscales.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateRow = (productId, changes) => {
    setRows((current) =>
      current.map((row) => (row.productId === productId ? { ...row, ...changes } : row))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.put("settings/payscales/", {
        items: rows.map((row) => ({
          product_id: row.productId,
          cost: row.cost === "" ? null : row.cost,
          tax_percent: row.taxPercent === "" ? null : row.taxPercent,
          level_6_amount: row.level6Amount === "" ? null : row.level6Amount,
        })),
      });
      setRows(
        (res.data.items || []).map((item) => ({
          productId: item.product_id,
          name: item.name,
          cost: displayNumber(item.cost),
          taxPercent: displayNumber(item.tax_percent),
          level6Amount: displayNumber(item.level_6_amount),
        }))
      );
      toast.success("Payscales saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save payscales.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="equipment-pricing-page" aria-labelledby="pricing-title">
      <header className="equipment-pricing-header">
        <div>
          <h1 id="pricing-title">Equipment Pay Scale</h1>
          <p>
            Set the equipment deduction for each pay level. Company cost is the equipment cost plus tax. Levels 1–5 add 0%, 10%, 15%, 20%, and 25%. Level 6 is the custom amount you enter. That total is deducted from the rep’s commission when the equipment is on a completed sale.
          </p>
        </div>
        <DisabledButtonWrapper
          isDisabled={isReadOnly || saving || loading}
          tooltipText={saving ? "Saving in progress..." : "Read-only mode: This action is disabled"}
        >
          <button
            type="button"
            className="pricing-save-button"
            onClick={handleSave}
            disabled={isReadOnly || saving || loading}
          >
            <Save size={16} />
            {saving ? "Saving..." : "Save"}
          </button>
        </DisabledButtonWrapper>
      </header>

      <div className="pricing-table-card">
        {loading ? (
          <p className="pricing-empty">Loading equipment…</p>
        ) : rows.length === 0 ? (
          <div className="pricing-empty">
            <p>No products yet. Add them on Products & Services and they will show up here.</p>
            <button type="button" onClick={() => navigate("/settings/products-services")}>
              Products & Services
            </button>
          </div>
        ) : (
          <div className="pricing-table-scroll">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>Equipment</th>
                  <th>Cost</th>
                  <th>Tax %</th>
                  <th>Company cost</th>
                  {LEVELS.map((level) => (
                    <th key={level.label}>
                      {level.label}
                      <span>{level.hint}</span>
                    </th>
                  ))}
                  <th>
                    Level 6
                    <span>Custom</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const priced = rowPrices(row.cost, row.taxPercent);
                  return (
                    <tr key={row.productId}>
                      <td>
                        <div className="equipment-name">
                          <span className="equipment-icon">
                            <Package size={16} />
                          </span>
                          {row.name}
                        </div>
                      </td>
                      <td>
                        <input
                          className="pricing-input"
                          value={row.cost}
                          onChange={(event) => updateRow(row.productId, { cost: event.target.value })}
                          disabled={isReadOnly}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td>
                        <input
                          className="pricing-input"
                          value={row.taxPercent}
                          onChange={(event) => updateRow(row.productId, { taxPercent: event.target.value })}
                          disabled={isReadOnly}
                          type="number"
                          min="0"
                          step="0.001"
                          placeholder="0"
                        />
                      </td>
                      <td className="pricing-calculated">{formatMoney(priced?.companyCost)}</td>
                      {(priced?.levels || [null, null, null, null, null]).map((amount, index) => (
                        <td key={`${row.productId}-${index}`} className="pricing-calculated">
                          {formatMoney(amount)}
                        </td>
                      ))}
                      <td>
                        <input
                          className="pricing-input"
                          value={row.level6Amount}
                          onChange={(event) => updateRow(row.productId, { level6Amount: event.target.value })}
                          disabled={isReadOnly}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
